import { useEffect, useState } from 'react'

import { Section, Text } from '@factorialco/deprecated-design-system'
import {
  SalaryAdvanceSettingsPageDocument,
  SaveSalaryAdvanceCompanySettingDocument,
  type SaveSalaryAdvanceCompanySettingMutation,
} from 'generated/resources/graphql'

import { SingleRangeSlider } from 'modules/core/components/SingleRangeSlider'
import {
  MAX_CAP_PERCENTAGE,
  MIN_CAP_PERCENTAGE,
  normalizeCapPercentage,
} from 'modules/salary_advance/lib/companySettings'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type Props = {
  currentCapPercentage: number
  requestDeadlineDay: number
  serviceEnabled: boolean
  canEdit: boolean
}

const CapPercentageSetting = ({
  currentCapPercentage,
  requestDeadlineDay,
  serviceEnabled,
  canEdit,
}: Props) => {
  const normalizedCapPercentage = normalizeCapPercentage(currentCapPercentage)
  const [selectedCapPercentage, setSelectedCapPercentage] =
    useState<number>(normalizedCapPercentage)

  const [saveCompanySetting, { loading }] = useMutation(SaveSalaryAdvanceCompanySettingDocument, {
    extractorFn: (payload: SaveSalaryAdvanceCompanySettingMutation) =>
      payload.salaryAdvanceMutations?.saveSalaryadvanceCompanySetting ?? { errors: [] },
    refetchQueries: [SalaryAdvanceSettingsPageDocument],
  })

  useEffect(() => {
    setSelectedCapPercentage(normalizeCapPercentage(currentCapPercentage))
  }, [currentCapPercentage])

  const isDirty = selectedCapPercentage !== normalizedCapPercentage

  const handleSave = async () => {
    const result = await saveCompanySetting({
      variables: {
        advanceCapMonthlyWagePercent: selectedCapPercentage,
        requestDeadlineDay,
        serviceEnabled,
      },
    })
    const { success } = handleResult(result, () => {})

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  return (
    <Section.Form
      illustration="money-bag"
      title={i18n.t('salary_advance.settings.cap_percentage.new_title')}
      description={i18n.t('salary_advance.settings.cap_percentage.new_description')}
      submitLabel={i18n.t('common.save')}
      isDirty={isDirty}
      onSubmit={handleSave}
      disabled={!canEdit || loading}
    >
      <Text.Normal weight="semibold" size="200">
        {i18n.t('salary_advance.settings.cap_percentage.new_label', {
          percentage: selectedCapPercentage,
        })}
      </Text.Normal>
      <SingleRangeSlider
        min={MIN_CAP_PERCENTAGE}
        max={MAX_CAP_PERCENTAGE}
        value={selectedCapPercentage}
        onChange={setSelectedCapPercentage}
        readonly={!canEdit}
      />
    </Section.Form>
  )
}

export default CapPercentageSetting
